import { Multiselect } from 'multiselect-react-dropdown';
import { variables } from 'src/Tenants';
import {
  ContactFormActionsContext,
  useContactFormActionsFromContext,
  useContactFormDataFromContext,
} from 'src/SharedCommonContext/ContactFormContext';
import { Property, DetailedSuite } from 'src/components/ContactForm/types';
import React, { useMemo } from 'react';
import Select from 'react-select';
import { USER_ROLES } from 'src/common/constants';
import { toast } from 'react-toastify';
import {
  PropertiesActionsContext,
  PropertiesDataContext,
  usePropertiesActionsFromContext,
  usePropertiesDataFromContext,
} from 'src/pages/Contacts/ContactsContext/PropertiesForContactsContext';

interface PropertySuiteSelectorProps {
  user: any;
  isFromPropertyOrSuite: boolean;
  selectedContact: any;
  formType: any;
  editingExistingContactInTable?: boolean;
  currentSuiteIdForAddingExistingHomeowner?: string;
}

const PropertySuiteSelector = ({
  user,
  isFromPropertyOrSuite,
  selectedContact,
  formType,
  editingExistingContactInTable,
  currentSuiteIdForAddingExistingHomeowner,
}: PropertySuiteSelectorProps) => {
  const { contactFormData, errors } = useContactFormDataFromContext();

  const {
    setContactFormData,
    clearSuites,
    addNewSuite,
    removeSuite,
    addPropertyToSelected,
    removePropertyFromSelected,
    clearSuitesInFormForRemovedProperty,
  }: ContactFormActionsContext = useContactFormActionsFromContext();

  const { availableProperties, availableSuites }: PropertiesDataContext =
    usePropertiesDataFromContext();

  const {
    fetchPropertySuites,
    fetchPropertiesSuites,
    setData,
    updateDisplayedSuitesInUIUponRemovingProperty,
    updateDisplayedSuitesInUIUponAddingProperty,
  }: PropertiesActionsContext = usePropertiesActionsFromContext();

  const { userRole = '' } = contactFormData;

  const PROPERTY_SUITE_ROLES = [
    'Home Owner',
    'Realtor',
    'Occupant',
    'Service Manager',
    'Service Provider',
    'Property Developer',
  ];
  const MULTI_SELECT_ROLES = [
    'Home Owner',
    'Realtor',
    'Service Provider',
    'Property Developer',
  ];
  const UNIT_SELECTION_ROLES = ['Home Owner', 'Realtor'];

  const isApplicableRole: boolean = PROPERTY_SUITE_ROLES.includes(userRole);
  const usesMultiSelect: boolean = MULTI_SELECT_ROLES.includes(userRole);
  const showsUnitSelection: boolean = UNIT_SELECTION_ROLES.includes(userRole);
  const showsSuiteDropdown: boolean = userRole === 'Occupant';

  if (!isApplicableRole) {
    return null;
  }

  const safeAvailableProperties: Property[] = availableProperties || [];
  const safeAvailableSuites: DetailedSuite[] = availableSuites || [];

  const propertyOptions = useMemo(() => {
    return safeAvailableProperties.map((property: Property) => ({
      Id: property.propertyId || property.id,
      name: property.name,
    }));
  }, [safeAvailableProperties]);

  const selectedUserPropertiesToDisplayBubbles = useMemo(() => {
    return contactFormData?.properties?.map((property: any) => ({
      Id: property.propertyId || property.id,
      name: property.name,
    }));
  }, [contactFormData.properties]);

  const suiteOptions = useMemo(() => {
    return safeAvailableSuites.map((suite: DetailedSuite) => ({
      Id: suite.id,
      name: suite.propertyName + ' - ' + suite.number,
      propertyId: suite.propertyId,
    }));
  }, [safeAvailableSuites]);

  const selectedSuitesForDisplay = useMemo(() => {
    return contactFormData.Suites.map((suite: any) => ({
      Id: suite.Id,
      name: suite.name,
      propertyId: suite.propertyId,
    }));
  }, [contactFormData.Suites]);

  const propertySelectOptions = useMemo(() => {
    return safeAvailableProperties.map((property: Property) => ({
      value: property.propertyId || property.id,
      label: property.name,
    }));
  }, [safeAvailableProperties]);

  const selectedPropertyValue = useMemo(() => {
    /* TODO: Fix this hack - the property selection system is inconsistent between
     * multiselect (uses contactFormData.properties array) and single select
     * (uses contactFormData.propertyId). When isFromPropertyDeveloper=true,
     * we force single select to use the first property from the properties array. */
    if (
      isFromPropertyOrSuite &&
      contactFormData.properties &&
      contactFormData.properties.length > 0
    ) {
      const firstProperty = contactFormData.properties[0];

      return {
        value: firstProperty.propertyId || firstProperty.id,
        label: firstProperty.name,
      };
    }

    return (
      propertySelectOptions.find(
        (option) => option.value === contactFormData.propertyId
      ) || null
    );
  }, [
    propertySelectOptions,
    contactFormData.propertyId,
    isFromPropertyOrSuite,
    contactFormData.properties,
  ]);

  const selectSuiteOptions = useMemo(() => {
    return safeAvailableSuites.map((suite: DetailedSuite) => ({
      value: suite.id,
      label: suite.number,
    }));
  }, [safeAvailableSuites]);

  const selectedSuiteValue = useMemo(() => {
    return (
      selectSuiteOptions.find(
        (option) => option.value === contactFormData.suiteId
      ) || null
    );
  }, [selectSuiteOptions, contactFormData.suiteId]);

  const handlePropertySelection = async (
    newSelectedProps: any[],
    action: 'add' | 'remove'
  ) => {
    // Pre-process: Get current state and determine role requirements.
    const currentSelectedProperties = contactFormData?.properties || [];
    const requiresSuiteManagement =
      contactFormData.userRole === USER_ROLES.HOME_OWNER ||
      contactFormData.userRole === USER_ROLES.REALTOR;

    try {
      if (action === 'add') {
        await handlePropertyAddition(
          newSelectedProps,
          currentSelectedProperties,
          requiresSuiteManagement
        );
      } else {
        await handlePropertyRemoval(
          newSelectedProps,
          currentSelectedProperties,
          requiresSuiteManagement
        );
      }
    } catch (error) {
      console.error(`Error during property ${action} orchestration:`, error);
      toast.error('Error updating property selection. Please try again.', {
        autoClose: 5000,
      });

      // Revert changes on error
      if (action === 'add') {
        removePropertyFromSelected(currentSelectedProperties);
      }
    }
  };

  const isPropertyAlreadySelected = (newProperty: any, currentSelectedProperties: any) => {
    /* Todo: Pretty sure there's a data inconsistency coming from the API here which is the root cause
     * of this one but this hack should do for now until we get time to further investigate. */
    const newPropertyId: any = newProperty.Id || newProperty.id || newProperty.propertyId;

    return currentSelectedProperties.some((currentProp) => {
      const currentPropId = currentProp.propertyId || currentProp.id;

      return currentPropId === newPropertyId;
    });
  }

  const handlePropertyAddition = async (
    newSelectedProps: any[],
    currentSelectedProperties: any[],
    requiresSuiteManagement: boolean
  ) => {
    const newlyAddedProperty = newSelectedProps.find((newProp) => {
      const alreadySelected = isPropertyAlreadySelected(newProp, currentSelectedProperties);
      return !alreadySelected;
    });

    if (!newlyAddedProperty) {
      console.warn('No new property found in selection');
      return;
    }

    addPropertyToSelected(newSelectedProps);

    if (!requiresSuiteManagement) {
      return;
    }

    await updateDisplayedSuitesInUIUponAddingProperty(newlyAddedProperty);

    const propertyIds = newSelectedProps.map((prop: any) => prop.Id || prop.id);
    const apiParams = {
      Properties: propertyIds,
      UserId: user.id,
      Role: user.roles[0].role.name,
    };

    await fetchPropertiesSuites(apiParams);

    setData((prevState: any) => ({
      ...prevState,
      propertiesList: newSelectedProps,
    }));
  };

  const handlePropertyRemoval = async (
    newSelectedProps: any[],
    currentSelectedProperties: any[],
    requiresSuiteManagement: boolean
  ) => {
    const removedProperty = currentSelectedProperties.find((currentProp) => {
      const stillSelected = isPropertyAlreadySelected(currentProp, newSelectedProps);
      return !stillSelected;
    });

    if (!removedProperty) {
      console.warn('No removed property found in selection');
      return;
    }

    if (userRole !== 'Service Provider') {
      clearSuitesInFormForRemovedProperty(removedProperty);
    }

    updateDisplayedSuitesInUIUponRemovingProperty(removedProperty);
    removePropertyFromSelected(newSelectedProps);

    // Guard: Handle complete removal case
    if (newSelectedProps.length === 0) {
      setData((prevState: any) => ({
        ...prevState,
        propertiesList: [],
        suitesList: [],
      }));
      return;
    }

    // Guard: Early return if role doesn't need suite management
    if (!requiresSuiteManagement) {
      return;
    }

    const propertyIds = newSelectedProps.map((prop: any) => prop.Id || prop.id);

    const apiParams = {
      Properties: propertyIds,
      UserId: user.id,
      Role: user.roles[0].role.name,
    };

    await fetchPropertiesSuites(apiParams);

    setData((prevState: any) => ({
      ...prevState,
      propertiesList: newSelectedProps,
    }));
  };

  const disablePropertiesSelector: boolean =
    isFromPropertyOrSuite && editingExistingContactInTable;

  console.log("propertyOptions: ", propertyOptions)

  return (
    <div style={{ marginBottom: '2.5rem' }}>
      <div className="input__errors">
        <div className="input__error">{errors?.type}</div>
      </div>
      {/* --- Property Selection (Multi-Select Roles) --- */}
      {usesMultiSelect ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className="reporting__heading" style={{ color: 'black' }}>
              Properties
            </div>
            {userRole !== 'Service Provider' && (
              <div className="picker__required" />
            )}
          </div>
          {/* Special view for adding existing contact to a property */}
          {isFromPropertyOrSuite && !editingExistingContactInTable ? (
            <div
              style={{
                display: 'inline-block',
                backgroundColor: '#2196F3',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '600',
                marginTop: '8px',
                boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
              }}
            >
              {contactFormData.properties?.[0]?.name || 'Property'}
            </div>
          ) : (
            /* Normal Multiselect for all other cases */
            <>
              {disablePropertiesSelector && (
                <div
                  style={{
                    color: '#d0285b',
                    marginBottom: '1.5rem',
                    fontSize: '1.4rem',
                    fontStyle: 'italic',
                  }}
                >
                  Read-only view. To modify properties for a contact, please
                  visit{' '}
                  <a
                    href="/app/contacts"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#0c73c9',
                      textDecoration: 'underline',
                      fontWeight: '600',
                    }}
                  >
                    Contacts
                  </a>
                  .
                </div>
              )}
              <Multiselect
                onRemove={(e: any) => handlePropertySelection(e, 'remove')}
                onSelect={(e: any) => handlePropertySelection(e, 'add')}
                key="Id"
                selectedValues={selectedUserPropertiesToDisplayBubbles}
                options={propertyOptions}
                label={isFromPropertyOrSuite ? '' : 'Select Properties'}
                displayValue="name"
                placeholder={
                  disablePropertiesSelector ? '' : 'Select Properties'
                }
                disablePreSelectedValues={
                  (isFromPropertyOrSuite && !editingExistingContactInTable) ||
                  disablePropertiesSelector
                }
                disable={
                  (isFromPropertyOrSuite && !editingExistingContactInTable) ||
                  disablePropertiesSelector
                }
              />
            </>
          )}
        </>
      ) : (
        /* --- Property Selection (Single-Select Roles) --- */
        <>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className="reporting__heading" style={{ color: 'black' }}>
              Property
            </div>
            {userRole !== 'Service Provider' && (
              <div className="picker__required" />
            )}
          </div>
          {/* Special view for adding existing contact to a property (single select) */}
          {isFromPropertyOrSuite && !editingExistingContactInTable ? (
            <div
              style={{
                display: 'inline-block',
                backgroundColor: '#2196F3',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '600',
                marginTop: '8px',
                boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
              }}
            >
              {contactFormData.properties?.[0]?.name || 'Property'}
            </div>
          ) : (
            /* Normal Select for all other cases */
            <Select
              value={selectedPropertyValue}
              onChange={async (e: any) => {
                const propertyId = e.value;
                clearSuites();
                await fetchPropertySuites(propertyId);
                setContactFormData({
                  propertyId: propertyId,
                  suiteId: '',
                  Suites: [],
                });
              }}
              options={propertySelectOptions}
              placeholder={isFromPropertyOrSuite ? '' : 'Select a Property'}
              isDisabled={isFromPropertyOrSuite}
            />
          )}
        </>
      )}
      {/* --- Unit/Suite Selection Section --- */}
      <div style={{ marginTop: '1rem' }}>
        {/* Unit Selection (Multi-Select for Home Owner/Realtor) */}
        {showsUnitSelection && (
          <>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div className="reporting__heading" style={{ color: 'black' }}>
                Unit(s)
              </div>
              {userRole !== 'Service Provider' && (
                <div className="picker__required" />
              )}
            </div>

            {disablePropertiesSelector && (
              <div
                style={{
                  color: '#d0285b',
                  marginBottom: '1.5rem',
                  fontSize: '1.4rem',
                  fontStyle: 'italic',
                }}
              >
                Read-only view. To modify units for a contact, please visit{' '}
                <a
                  href="/app/contacts"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#0c73c9',
                    textDecoration: 'underline',
                    fontWeight: '600',
                  }}
                >
                  Contacts
                </a>
                .
              </div>
            )}
            {/* Special view for adding existing contact to suite */}
            {isFromPropertyOrSuite && !editingExistingContactInTable ? (
              <div
                style={{
                  display: 'inline-block',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginTop: '8px',
                  boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
                }}
              >
                {`${contactFormData.properties?.[0]?.name} - ${currentSuiteIdForAddingExistingHomeowner}` ||
                  'Suite'}
              </div>
            ) : (
              /* Normal Multiselect for all other cases */
              <Multiselect
                id="suites"
                options={suiteOptions}
                selectedValues={selectedSuitesForDisplay}
                displayValue="name"
                label={'Select Units'}
                placeholder={
                  disablePropertiesSelector
                    ? ''
                    : `Select ${variables?.suiteName || 'Suite'}`
                }
                disable={
                  safeAvailableSuites.length === 0 || isFromPropertyOrSuite
                }
                onSelect={(e: any) => addNewSuite(e)}
                onRemove={(e: any) => removeSuite(e)}
                disablePreSelectedValues={isFromPropertyOrSuite}
              />
            )}
          </>
        )}
        {/* Suite Selection (Single-Select for Occupant) */}
        {showsSuiteDropdown && (
          <>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div className="reporting__heading" style={{ color: 'black' }}>
                Unit
              </div>
              {userRole !== 'Service Provider' && (
                <div className="picker__required" />
              )}
            </div>
            {/* Special view for adding existing contact to suite */}
            {isFromPropertyOrSuite && !editingExistingContactInTable ? (
              <div
                style={{
                  display: 'inline-block',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginTop: '8px',
                  boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
                }}
              >
                {`${contactFormData.properties?.[0]?.name} - ${currentSuiteIdForAddingExistingHomeowner}` ||
                  selectedSuiteValue?.label ||
                  'Suite'}
              </div>
            ) : (
              /* Normal Select dropdown for all other cases */
              <Select
                value={selectedSuiteValue}
                onChange={(e: any) => setContactFormData({ suiteId: e.value })}
                options={selectSuiteOptions}
                isDisabled={
                  !contactFormData.propertyId ||
                  selectSuiteOptions.length === 0 ||
                  isFromPropertyOrSuite
                }
                placeholder={
                  !contactFormData.propertyId
                    ? 'Please select a property to display units'
                    : selectSuiteOptions.length === 0
                      ? 'No suites available for this property'
                      : 'Select a suite'
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PropertySuiteSelector;
